import { makeRequest } from '@dealstack/frontend/shared/dataExchange/ApiLayer/apiLayer';
import * as ApiV1 from '@dealstack/shared/lib/common/dataExchange/apiV1';
import { HTTP_METHOD } from '@dealstack/shared/lib/common/dataExchange/common';
import { backendCodedErrorFrom } from '@dealstack/frontend/shared/dataExchange/services/backendCodedError';
import {
	DataUserResponse,
	UpdatePhoneDto,
	ChangePasswordDto,
	ChangeEmailRequestDto,
	ChangeEmailDto,
	DeleteChangeEmailRequestDto,
	CancelChangeEmailDto,
	InvitationRenewalRes,
	RenewInvitationDto,
	Auth2FAChallengeAndFactorResponse,
	VerifyChallenge2FADto,
	VerifyChallengeResponse,
	DeleteAuthFactorDto,
	ValidateMagicCodeDto,
	ValidateAuthResponse,
	SendVerificationDto,
} from '@dealstack/shared/lib/generated';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths';
import { getGlobalConfig } from '@dealstack/frontend/shared/globalConfig';
import { apiClient } from '@dealstack/frontend/api/client';

// use make Request
export const getUser = async (): Promise<{
	payload: DataUserResponse;
}> => {
	const globalConfig = getGlobalConfig();

	const response = await apiClient(`${globalConfig.apiUrl}${API_PATHS.USER_GET}`, {
		method: HTTP_METHOD.GET,
	});
	const responseBody = await response.json();
	if (!response.ok) {
		throw responseBody;
	}
	return responseBody;
};

export const logoutApi = async (): Promise<void> => {
	const globalConfig = getGlobalConfig();
	const response = await apiClient(`${globalConfig.apiUrl}${API_PATHS.AUTH_LOGOUT_POST}`, {
		method: HTTP_METHOD.POST,
		headers: {
			'Content-Type': 'application/json',
		},
	});

	const responseBody = await response.json();

	if (!response.ok) {
		throw responseBody;
	}
};

export const signOutApi = async (): Promise<void> => {
	const globalConfig = getGlobalConfig();

	await apiClient(`${globalConfig.apiUrl}${API_PATHS.AUTH_SIGN_OUT_POST}`, {
		method: HTTP_METHOD.POST,
		headers: {
			'Content-Type': 'application/json',
		},
	});
};

export const updateInvestorPhone = async (body: UpdatePhoneDto) => {
	return await makeRequest<ApiV1.Response<void>>({
		endpoint: API_PATHS.AUTH_PROFILE_PHONE_POST,
		method: HTTP_METHOD.POST,
		body: JSON.stringify(body),
	});
};

export const changePasswordV2 = async (params: ChangePasswordDto) => {
	const globalConfig = getGlobalConfig();
	const response = await apiClient(
		`${globalConfig.apiUrl}${API_PATHS.AUTH_CHANGE_PASSWORD_POST}`,
		{
			method: HTTP_METHOD.POST,
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify(params),
		}
	);
	if (!response.ok) {
		throw await backendCodedErrorFrom(response);
	}
};

export const requestChangeEmail = async (params: ChangeEmailRequestDto) => {
	const globalConfig = getGlobalConfig();
	const response = await apiClient(
		`${globalConfig.apiUrl}${API_PATHS.AUTH_CHANGE_EMAIL_REQUEST_POST}`,
		{
			method: HTTP_METHOD.POST,
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify(params),
		}
	);
	if (!response.ok) {
		throw await backendCodedErrorFrom(response);
	}
};

export const changeEmail = async (params: ChangeEmailDto) => {
	const globalConfig = getGlobalConfig();
	const response = await apiClient(`${globalConfig.apiUrl}${API_PATHS.AUTH_CHANGE_EMAIL_POST}`, {
		method: HTTP_METHOD.POST,
		headers: {
			'Content-Type': 'application/json',
		},
		body: JSON.stringify(params),
	});
	if (!response.ok) {
		throw response;
	}
};

export const deleteEmailReq = async (params: DeleteChangeEmailRequestDto) => {
	const globalConfig = getGlobalConfig();
	const response = await apiClient(
		`${globalConfig.apiUrl}${API_PATHS.AUTH_CHANGE_EMAIL_DELETE}`,
		{
			method: HTTP_METHOD.DELETE,
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify(params),
		}
	);
	if (!response.ok) {
		throw response;
	}
};

export const renewInvitation = async (
	params: RenewInvitationDto
): Promise<InvitationRenewalRes | undefined> => {
	const globalConfig = getGlobalConfig();
	const response = await apiClient(
		`${globalConfig.apiUrl}${API_PATHS.AUTH_INVITATION_RENEW_POST}`,
		{
			method: HTTP_METHOD.POST,
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify(params),
		}
	);
	if (!response.ok) {
		throw await backendCodedErrorFrom(response);
	}

	const body: ApiV1.Response<InvitationRenewalRes> = await response.json();

	return body.payload;
};

export const cancelChangeEmail = async (params: CancelChangeEmailDto) => {
	const globalConfig = getGlobalConfig();
	const response = await apiClient(
		`${globalConfig.apiUrl}${API_PATHS.AUTH_CHANGE_EMAIL_CANCEL_POST}`,
		{
			method: HTTP_METHOD.POST,
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify(params),
		}
	);
	if (!response.ok) {
		throw await backendCodedErrorFrom(response);
	}
};

export const setup2FA = async (endpoint: string) => {
	const response = await makeRequest<ApiV1.Response<Auth2FAChallengeAndFactorResponse>>(
		{
			endpoint,
			method: HTTP_METHOD.POST,
		},
		{}
	);
	return response;
};

export const verifyChallenge2FA = async (body: VerifyChallenge2FADto) => {
	return await makeRequest<ApiV1.Response<VerifyChallengeResponse>>({
		endpoint: API_PATHS.AUTH_2FA_VERIFY_CHALLENGE_POST,
		method: HTTP_METHOD.POST,
		body: JSON.stringify(body),
	});
};

export const deleteAuthFactor = async (body: DeleteAuthFactorDto) => {
	return await makeRequest<ApiV1.Response<void>>({
		endpoint: API_PATHS.AUTH_2FA_DELETE_FACTOR_DELETE,
		method: HTTP_METHOD.DELETE,
		body: JSON.stringify(body),
	});
};

export const sendMagicAuthVerificationCode = async (body: SendVerificationDto) => {
	return await makeRequest<ApiV1.Response<void>>({
		endpoint: API_PATHS.AUTH_EMAIL_SEND_MAGIC_CODE_POST,
		method: HTTP_METHOD.POST,
		body: JSON.stringify(body),
	});
};

export const validateMagicAuthVerificationCode = async (body: ValidateMagicCodeDto) => {
	return await makeRequest<ApiV1.Response<ValidateAuthResponse>>({
		endpoint: API_PATHS.AUTH_EMAIL_VALIDATE_MAGIC_CODE_POST,
		method: HTTP_METHOD.POST,
		body: JSON.stringify(body),
	}).then((response) => response.payload);
};
